import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import svgPaths from "../../imports/svg-3alwwuq884";
import { PrimaryButton } from '../components/PrimaryButton';
import { FloatingLabelInput } from '../components/FloatingLabelInput';
import { SignUpHeader } from '../components/SignUpHeader';
import { AuthTabs } from '../components/AuthTabs';
import { OtpVerify } from '../components/OtpVerify';
import {
  customerApiErrorMessage,
  forgotResetPassword,
  forgotSendOtp,
  forgotVerifyOtp,
} from '../../lib/customer-api';
import { toast } from '../../lib/toast';

type Step = 'email' | 'otp' | 'password' | 'done';

const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const passwordValid = (pw: string) => pw.length >= 8 && /\d/.test(pw);

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Captured after the first call so we can re-use them on subsequent
  // steps without making the user retype.
  const [referenceNumber, setReferenceNumber] = useState<string | null>(null);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);

  const [rememberToken, setRememberToken] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);

  const handleSendOtp = async () => {
    if (!email || !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError(null);
    setSubmitting(true);
    try {
      const res = await forgotSendOtp(email.trim().toLowerCase());
      // Backend returns a generic 200 even when the email doesn't exist
      // to avoid enumeration. When there's no reference_number, stay on
      // this step but show a toast so the user knows to check their email
      // (if it exists). Never reveal whether the email is registered.
      if (res.reference_number) {
        setReferenceNumber(res.reference_number);
        toast.success('OTP sent — check your email.');
        setStep('otp');
      } else {
        toast.success('If the email exists, an OTP has been sent. Please check your email.');
      }
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not send OTP.');
      setEmailError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!referenceNumber) return;
    if (!/^\d{6}$/.test(otp)) {
      setOtpError('Enter the 6-digit code from your email.');
      return;
    }
    setOtpError(null);
    setSubmitting(true);
    try {
      const res = await forgotVerifyOtp({
        reference_number: referenceNumber,
        email: email.trim().toLowerCase(),
        otp,
      });
      setRememberToken(res.remember_token);
      toast.success('OTP verified — pick a new password.');
      setStep('password');
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Invalid or expired OTP.');
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!rememberToken) return;
    if (!passwordValid(newPassword)) {
      setPasswordError('At least 8 characters and one number.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    setPasswordError(null);
    setSubmitting(true);
    try {
      await forgotResetPassword({
        remember_token: rememberToken,
        new_password: newPassword,
      });
      toast.success('Password reset successfully — you can sign in now.');
      setStep('done');
    } catch (err) {
      const msg = customerApiErrorMessage(err, 'Could not reset password.');
      setPasswordError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SignUpHeader mode="signup" showSwitchButton={false} />

      <main className="flex-1 flex flex-col items-center justify-center py-12 px-4">
        <AuthTabs mode="forgot" active="customer" />
        <div className="w-full max-w-[420px] flex flex-col items-center">
          {/* Lock Icon */}
          <div className="relative w-[181px] h-[124px] mb-8">
            <svg className="absolute inset-0 w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 181 124">
              <g>
                <path d={svgPaths.pab57f00} fill="white" />
                <path d={svgPaths.pab57f00} fill="#3A3335" fillOpacity="0.04" />
              </g>
            </svg>
            <div className="absolute left-[29.28%] right-[30.39%] top-[41.94%] bottom-[0.81%]">
              <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 73 73">
                <circle cx="36.5" cy="36.5" fill="#C6D8D3" r="36.5" />
              </svg>
            </div>
            <div className="absolute left-[39.78%] right-[39.45%] top-[65.32%] bottom-[17.02%]">
              <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 34 22">
                <path clipRule="evenodd" d={svgPaths.p94e0f00} fill="white" fillRule="evenodd" />
              </svg>
            </div>
            <div className="absolute left-[43.65%] right-[45.86%] top-[51.61%] bottom-[33.06%]">
              <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 19 19">
                <path clipRule="evenodd" d={svgPaths.p39c1cd00} fill="white" fillRule="evenodd" />
              </svg>
            </div>
            <div className="absolute left-[46.52%] right-[51.27%] top-[70.97%] bottom-[25.81%]">
              <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 4 4">
                <circle cx="2" cy="2" fill="white" r="2" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8 max-w-[334px]">
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] tracking-[-0.0857px] mb-4">
              {step === 'email' ? (
                <>
                  <p className="leading-[36px]">Lost your password?</p>
                  <p className="leading-[36px]">Enter your details to recover</p>
                </>
              ) : step === 'otp' ? (
                <p className="leading-[36px]">Enter the 6-digit code</p>
              ) : step === 'password' ? (
                <p className="leading-[36px]">Set a new password</p>
              ) : (
                <p className="leading-[36px]">Password reset</p>
              )}
            </h1>
            <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
              {step === 'email'
                ? 'Enter your email to proceed further'
                : step === 'otp'
                ? `We've emailed a code to ${email}. It expires in 1 minute.`
                : step === 'password'
                ? 'Pick a strong password with at least 8 characters and a number.'
                : 'You can sign in with your new password now.'}
            </p>
          </div>

          <div className="w-full max-w-[420px] flex flex-col gap-6">
            {step === 'email' ? (
              <>
                <FloatingLabelInput
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(value) => {
                    setEmail(value);
                    setEmailError(null);
                  }}
                  icon={``}
                  className="w-full"
                  error={!!emailError}
                  errorMessage={emailError ?? ''}
                />
                <PrimaryButton
                  onClick={handleSendOtp}
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Send OTP'}
                </PrimaryButton>
              </>
            ) : null}

            {step === 'otp' ? (
              <OtpVerify
                value={otp}
                onChange={(v) => {
                  setOtp(v);
                  setOtpError(null);
                }}
                onVerify={handleVerifyOtp}
                onResend={handleSendOtp}
                submitting={submitting}
                error={!!otpError}
              />
            ) : null}

            {step === 'password' ? (
              <>
                <FloatingLabelInput
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(v) => {
                    setNewPassword(v);
                    setPasswordError(null);
                  }}
                  icon={``}
                  className="w-full"
                  error={!!passwordError}
                  errorMessage={passwordError ?? ''}
                />
                <FloatingLabelInput
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(v) => {
                    setConfirmPassword(v);
                    setPasswordError(null);
                  }}
                  icon={``}
                  className="w-full"
                />
                <PrimaryButton
                  onClick={handleResetPassword}
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? 'Updating…' : 'Update password'}
                </PrimaryButton>
              </>
            ) : null}

            {step === 'done' ? (
              <PrimaryButton
                onClick={() => navigate('/signin/customer')}
                className="w-full"
              >
                Sign in
              </PrimaryButton>
            ) : null}

            <div className="text-center">
              <Link
                to="/signin/customer"
                className="font-['DM_Sans',sans-serif] font-bold leading-[21px] text-[#8e2a2f] text-[14px] tracking-[-0.1px] hover:underline"
                style={{ fontVariationSettings: "'opsz' 14" }}
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
